import React, { FC, useContext, useEffect, useState } from 'react';

import { ProvidersContext } from '../../contexts/providers.context';
import { SelectorLabels } from './selector.labels';
import { SelectorProps } from './selector.type';

/**
 * @description This component retrieves a list of (radio) options for the user to select
 * @param props {SelectorProps} this includes a method and a list of values to be shown
 */
export const SelectorItems: FC<SelectorProps> = ({ items, method, id }) => {
    if (!items.length) {
        return (
            <p className='c-compare-and-select__description'>
                {SelectorLabels.noItems}
            </p>
        );
    }

    const config = useContext(ProvidersContext);
    const brand = config.brand.toLowerCase();
    const [checked, setChecked] = useState(items[0].value);

    /**
     * When the items prop is change this useEffect will add or remove the style
     * class to show or hide the gradient div in the payment and percentage
     * selectors, it will depend on of the number of items.
     */
    useEffect(() => {
        setChecked(items[0].value);

        const gradientContainer = document.getElementById(`gradient-${id}`);
        const six = 6;
        if (items.length < six) {
            gradientContainer?.classList.remove(
                'gradient-show',
                `gradient-${brand}`,
            );
            return;
        }
        gradientContainer?.classList.add(`gradient-${brand}`);
    }, [items]);

    /**
     * This function make the functionality to show or hide the
     * gradient div in the payment and percentage selectors when
     * the position of the scroll change.
     */
    const showAndHideTheGradientDiv = (): void => {
        const selectorContainer = document.getElementById(
            `mfc-selector-container-${id}`,
        );
        const scrollTop = selectorContainer?.scrollTop;
        const gradientContainer = document.getElementById(`gradient-${id}`);

        if (scrollTop === 0) {
            gradientContainer?.classList.remove('gradient-hide');
            gradientContainer?.classList.add('gradient-show');
            return;
        }

        gradientContainer?.classList.remove(`gradient-${brand}`);
        gradientContainer?.classList.remove('gradient-show');
        gradientContainer?.classList.add('gradient-hide');
    };

    /**
     * Items provided will be made into (radio) buttons for the user to select. When clicked,
     * selected item will be highlighted also this will return the selected value.
     */
    const selectorItems: Array<JSX.Element> = items.map((item, index) => {
        const onChangeHandler = (itemId) => {
            setChecked(itemId);
            method({
                items,
                itemId: items.filter((val) => val.value === itemId)[0].id,
            });
        };
        return (
            <div
                className={`o-layout__item selectors__left--padding 
                u-1/6 u-pl-none u-pr-xxsmall u-pb-xsmall u-m-none u-1/1@s`}
                key={index}
            >
                <input
                    className='c-compare-and-select__hidden-input'
                    type='radio'
                    checked={checked === item.value}
                    name={`compare-and-select-${id}`}
                    id={item.value}
                    onChange={() => onChangeHandler(item.value)}
                />
                <div className='c-compare-and-select u-p-none u-m-none'>
                    <label
                        htmlFor={item.value}
                        className='label-compare-and-select u-text-center u-m-none u-p-small'
                    >
                        {item.value}
                    </label>
                </div>
            </div>
        );
    });

    return (
        <div
            className='c-compare-and-select-container container-selectors'
            id={`mfc-selector-container-${id}`}
            data-testid={`mfc-selector-container-${id}`}
            onScroll={showAndHideTheGradientDiv}
        >
            <div id={`gradient-${id}`} className={`gradient-${brand}`}></div>
            <div className='o-layout o-layout--left u-ml-none'>
                {selectorItems}
            </div>
        </div>
    );
};
